import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  TrendingUp as ProgressIcon,
  Star as StarIcon,
  EmojiEvents as TrophyIcon,
  Lightbulb as TipIcon
} from '@mui/icons-material';
import habitService from '../services/habitService';

const CheckpointSystem = ({ studyPlan, onComplete }) => {
  const [todaysTasks, setTodaysTasks] = useState([]);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [showCheckpoint, setShowCheckpoint] = useState(false);
  const [currentStreak, setCurrentStreak] = useState(0);
  const [motivationalMessage, setMotivationalMessage] = useState('');

  useEffect(() => {
    if (studyPlan) {
      generateTodaysTasks();
      loadCompletedTasks();
      checkIfShouldShowCheckpoint();
      loadStreak();
    }
  }, [studyPlan]);

  const generateTodaysTasks = () => {
    if (!studyPlan?.schedule) return;

    const today = new Date();
    const dayOfWeek = today.getDay();
    const tasks = [];

    studyPlan.schedule.forEach(timeSlot => {
      const dayEvent = timeSlot.days[dayOfWeek];
      if (dayEvent?.subject) {
        tasks.push({
          id: `${dayOfWeek}-${timeSlot.time}`,
          time: timeSlot.time,
          subject: dayEvent.subject,
          activity: dayEvent.activity,
          completed: false,
          type: 'study'
        });
      }
    });

    // Add general study tasks
    tasks.push(
      {
        id: 'review-notes',
        time: 'Flexible',
        subject: 'Review',
        activity: 'Review yesterday\'s notes',
        completed: false,
        type: 'review'
      },
      {
        id: 'practice-problems',
        time: 'Flexible',
        subject: 'Practice',
        activity: 'Solve practice problems',
        completed: false,
        type: 'practice'
      },
      {
        id: 'plan-tomorrow',
        time: 'Evening',
        subject: 'Planning',
        activity: 'Plan tomorrow\'s study session',
        completed: false,
        type: 'planning'
      }
    );

    setTodaysTasks(tasks);
  };

  const loadCompletedTasks = () => {
    const today = new Date().toDateString();
    const saved = localStorage.getItem(`completedTasks_${today}`);
    if (saved) {
      const completed = JSON.parse(saved);
      setCompletedTasks(completed);
      
      // Update task completion status
      setTodaysTasks(prev => prev.map(task => ({
        ...task,
        completed: completed.includes(task.id)
      })));
    }
  };

  const checkIfShouldShowCheckpoint = () => {
    const today = new Date().toDateString();
    const lastCheckpoint = localStorage.getItem('lastCheckpoint');
    
    if (lastCheckpoint !== today) {
      setShowCheckpoint(true);
    }
  };

  const loadStreak = () => {
    const streak = localStorage.getItem('studyStreak') || '0';
    setCurrentStreak(parseInt(streak));
  };

  const handleTaskToggle = (taskId) => {
    const updatedTasks = todaysTasks.map(task => 
      task.id === taskId ? { ...task, completed: !task.completed } : task
    );
    setTodaysTasks(updatedTasks);

    const completed = updatedTasks.filter(task => task.completed).map(task => task.id);
    setCompletedTasks(completed);

    // Save to localStorage
    const today = new Date().toDateString();
    localStorage.setItem(`completedTasks_${today}`, JSON.stringify(completed));

    // Update streak if all tasks completed
    if (completed.length === todaysTasks.length && completed.length > 0) {
      updateStreak();
    }
  };

  const updateStreak = () => {
    const newStreak = currentStreak + 1;
    setCurrentStreak(newStreak);
    localStorage.setItem('studyStreak', newStreak.toString());
    
    // Show celebration message
    setMotivationalMessage(getStreakMessage(newStreak));
  };

  const getStreakMessage = (streak) => {
    const messages = {
      1: "🎉 First day complete! You're building a great habit!",
      3: "🔥 3-day streak! You're on fire!",
      7: "🌟 Week streak! You're unstoppable!",
      14: "💎 Two weeks! You're a study champion!",
      30: "🏆 Month streak! You're absolutely legendary!"
    };
    
    return messages[streak] || `🔥 ${streak}-day streak! Keep it up!`;
  };

  const handleCheckpointComplete = () => {
    setShowCheckpoint(false);
    localStorage.setItem('lastCheckpoint', new Date().toDateString());
    
    // Record study session in habit service
    if (completedTasks.length > 0) {
      const sessionData = {
        duration: completedTasks.length * 0.5, // Estimate 30 minutes per task
        subjects: [...new Set(todaysTasks.filter(task => task.completed).map(task => task.subject))],
        activities: ['checkpoint', 'daily_tasks'],
        completed: completedTasks.length === todaysTasks.length
      };
      
      habitService.recordStudySession(sessionData);
    }
    
    if (onComplete) {
      onComplete(completedTasks.length, todaysTasks.length);
    }
  };

  const getTaskIcon = (type) => {
    switch (type) {
      case 'study': return '📚';
      case 'review': return '📖';
      case 'practice': return '✏️';
      case 'planning': return '📋';
      default: return '📝';
    }
  };

  const getTaskColor = (type) => {
    switch (type) {
      case 'study': return 'bg-blue-500';
      case 'review': return 'bg-green-500';
      case 'practice': return 'bg-purple-500';
      case 'planning': return 'bg-orange-500';
      default: return 'bg-gray-500';
    }
  };

  const getMotivationalTip = () => {
    const tips = [
      "Take short breaks every 25 minutes to maintain focus",
      "Review your notes within 24 hours for better retention",
      "Teach what you've learned to someone else",
      "Use the Pomodoro Technique for maximum productivity",
      "Stay hydrated and get enough sleep for optimal learning"
    ];
    return tips[Math.floor(Math.random() * tips.length)];
  };

  const progressPercentage = todaysTasks.length > 0 ? (completedTasks.length / todaysTasks.length) * 100 : 0;

  if (!showCheckpoint) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200">
                Daily Checkpoint
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                {new Date().toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            </div>
            
            <div className="text-right">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {currentStreak}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Day Streak
              </div>
            </div>
          </div>
        </div>

        {/* Progress Section */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
              Today's Progress
            </h3>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {completedTasks.length} of {todaysTasks.length} completed
            </span>
          </div>
          
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 mb-4">
            <motion.div 
              className="bg-gradient-to-r from-blue-500 to-purple-600 h-3 rounded-full transition-all duration-500"
              initial={{ width: 0 }}
              animate={{ width: `${progressPercentage}%` }}
            />
          </div>
          
          {motivationalMessage && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2">
                <TrophyIcon className="w-5 h-5 text-green-500" />
                <p className="text-green-800 dark:text-green-200 font-medium">
                  {motivationalMessage}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Tasks Section */}
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">
            Today's Study Tasks
          </h3>
          
          <div className="space-y-3">
            {todaysTasks.map((task) => (
              <motion.div
                key={task.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className={`flex items-center gap-3 p-4 rounded-lg border transition-all duration-200 ${
                  task.completed
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700'
                    : 'bg-gray-50 dark:bg-gray-900/50 border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600'
                }`}
              >
                <button
                  onClick={() => handleTaskToggle(task.id)}
                  className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                    task.completed
                      ? 'bg-green-500 border-green-500 text-white'
                      : 'border-gray-300 dark:border-gray-600 hover:border-green-500'
                  }`}
                >
                  {task.completed && <CheckIcon className="w-4 h-4" />}
                </button>
                
                <div className={`w-3 h-3 rounded-full ${getTaskColor(task.type)}`}></div>
                
                <div className="flex-1">
                  <div className={`font-medium ${
                    task.completed 
                      ? 'text-gray-600 dark:text-gray-400 line-through' 
                      : 'text-gray-800 dark:text-gray-200'
                  }`}>
                    {task.subject}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {task.time} • {task.activity}
                  </div>
                </div>
                
                <span className="text-2xl">{getTaskIcon(task.type)}</span>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Study Tip */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <TipIcon className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-1">
                  Study Tip of the Day
                </h4>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  {getMotivationalTip()}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex gap-3">
          <button
            onClick={handleCheckpointComplete}
            className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-200"
          >
            Complete Checkpoint
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default CheckpointSystem; 